import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import SeverityChip from "../components/argus/SeverityChip";
import { Button } from "../components/ui/button";
import {
  ArrowRight, Shield, Globe, FileCode2, Package, Cloud, Eye, Loader2,
  CheckCircle2, AlertTriangle, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api/v1";

/**
 * /demo — anonymous public demo of ArgusSecure findings.
 *
 * Calls the same demo data the seed produces, but via a *public* read-only
 * endpoint. No signup, no scanning of arbitrary targets. Synthetic by design.
 */
export default function Demo() {
  const [loading, setLoading] = useState(true);
  const [demo, setDemo] = useState(null);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [err, setErr] = useState(null);

  // ---- Live simulator state ----
  const [sim, setSim] = useState(null); // {session_id, scan, findings}
  const [simBusy, setSimBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/demo/projects`)
      .then((r) => {
        setDemo(r.data);
        if (r.data?.projects?.length) {
          setActiveProjectId(r.data.projects[0].id);
        }
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load the demo data."))
      .finally(() => setLoading(false));
  }, []);

  // Poll the live demo scan every second while it's not yet completed/canceled.
  useEffect(() => {
    if (!sim?.session_id || !sim?.scan?.id) return;
    if (["completed", "canceled", "failed"].includes(sim?.scan?.status)) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/demo/scans/${sim.session_id}/${sim.scan.id}`);
        setSim((s) => ({ ...s, scan: r.data.scan, findings: r.data.findings }));
      } catch {
        clearInterval(t);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [sim?.session_id, sim?.scan?.id, sim?.scan?.status]);

  const startDemoScan = async () => {
    setSimBusy(true);
    try {
      const r = await axios.post(`${API}/demo/scans`, {
        modes: ["sast", "dast", "sca", "secrets", "iac", "container"],
      });
      setSim({ session_id: r.data.session_id, scan: r.data.scan, findings: [] });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't start the demo scan.");
    } finally {
      setSimBusy(false);
    }
  };
  const resetSim = () => setSim(null);

  const active = demo?.projects?.find((p) => p.id === activeProjectId);
  const findings = (demo?.findings || []).filter((f) => f.project_id === activeProjectId);

  const TypeIcon = (t) => (t === "cloud" ? Cloud : t === "dast" ? Globe : t === "sast" ? FileCode2 : Package);

  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col" data-testid="demo-page">
      <MarketingHeader />

      <section className="max-w-6xl mx-auto px-6 pt-20 pb-8 w-full">
        <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Live demo</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
          See ArgusSecure findings, <em className="text-gold">without signing up.</em>
        </h1>
        <p className="text-ink/70 mt-5 max-w-2xl text-base lg:text-lg leading-relaxed">
          Three pre-scanned demo projects — synthetic by design. Real OWASP 2025
          tagging, real evidence shape, real fix recommendations. Click any
          finding to inspect it.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={startDemoScan} disabled={simBusy || (sim && !["completed","canceled","failed"].includes(sim?.scan?.status))} className="bg-gold text-paper hover:bg-gold2 h-11 font-semibold" data-testid="demo-run-scan">
            {simBusy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Sparkles size={16} className="mr-2" />}
            Run live demo scan
          </Button>
          <Button asChild variant="outline" className="h-11 border-line bg-transparent">
            <Link to="/signup" data-testid="demo-cta-signup">
              Try your own targets <ArrowRight size={16} className="ml-2" />
            </Link>
          </Button>
          <Button asChild variant="outline" className="h-11 border-line bg-transparent">
            <Link to="/why-argus" data-testid="demo-cta-why">Why ArgusSecure →</Link>
          </Button>
        </div>
      </section>

      {/* Live simulator panel */}
      {sim && (
        <section className="max-w-6xl mx-auto px-6 pb-8 w-full" data-testid="demo-simulator-panel">
          <div className="rounded-xl border border-gold/30 bg-gold/5 p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">Synthetic demo scan · in progress</div>
                <h2 className="font-display text-2xl mt-1 text-ink">
                  Scan {sim.scan.id.slice(-6)} — <span className="text-gold capitalize">{sim.scan.status}</span>
                </h2>
                <p className="text-xs text-ink/55 mt-1 font-mono">modes: {sim.scan.modes.join(", ")}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-display text-gold" data-testid="demo-progress-pct">{sim.scan.progress}%</div>
                <div className="text-[10px] uppercase tracking-wider text-ink/50">progress</div>
                {sim.scan.status === "streaming" && (
                  <button onClick={() => axios.post(`${API}/demo/scans/${sim.session_id}/${sim.scan.id}/cancel`).then(resetSim)} className="text-xs text-ink/60 hover:text-gold mt-2" data-testid="demo-cancel">Cancel</button>
                )}
                {["completed","canceled","failed"].includes(sim.scan.status) && (
                  <button onClick={resetSim} className="text-xs text-ink/60 hover:text-gold mt-2 underline" data-testid="demo-reset">Run another</button>
                )}
              </div>
            </div>
            <div className="mt-4 h-1 bg-paper/40 rounded-full overflow-hidden">
              <div className="h-full bg-gold transition-all" style={{ width: `${sim.scan.progress}%` }} />
            </div>
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-wider text-ink/45 mb-2">
                Findings stream — {sim.findings?.length || 0} of {sim.scan.findings_count_total}
              </div>
              {(!sim.findings || sim.findings.length === 0) ? (
                <div className="text-xs text-ink/50 italic py-4">Engines warming up — findings will appear as soon as the simulator reports them.</div>
              ) : (
                <ul className="space-y-2" data-testid="demo-sim-findings">
                  {sim.findings.map((f) => (
                    <li key={f.id} className="rounded-lg border border-line bg-panel p-3 flex items-start gap-3">
                      <SeverityChip severity={f.severity} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink font-semibold truncate">{f.title}</div>
                        <div className="text-[11px] text-ink/55 mt-0.5 font-mono truncate">{f.asset}</div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {f.cwe_id && <span className="text-[9px] uppercase tracking-wider border border-line bg-paper/50 rounded-full px-1.5 py-0.5 text-ink/60">{f.cwe_id}</span>}
                          {f.owasp_category && <span className="text-[9px] uppercase tracking-wider border border-gold/30 bg-gold/5 rounded-full px-1.5 py-0.5 text-gold/90">{f.owasp_category.split(" — ")[0]}</span>}
                          <span className="text-[9px] uppercase tracking-wider border border-line bg-paper/50 rounded-full px-1.5 py-0.5 text-ink/60">{f.mode}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Honest-copy disclaimer */}
      <section className="max-w-6xl mx-auto px-6 pb-8 w-full">
        <div className="border border-gold/30 bg-gold/5 rounded-lg p-4 flex items-start gap-3" data-testid="demo-disclaimer">
          <AlertTriangle size={16} className="text-gold mt-0.5 shrink-0" />
          <div className="text-xs text-ink/80 leading-relaxed">
            <strong className="text-ink">About this demo.</strong>{" "}
            All findings below come from a synthetic codebase + dependency manifest +
            sample S3 configuration. ArgusSecure never scans arbitrary targets in
            demo mode — your own projects need explicit authorization and
            (for web apps) domain verification before any scan runs.
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 w-full">
        {loading && (
          <div className="rounded-xl border border-line bg-panel p-12 text-center text-ink/60 flex items-center justify-center gap-3" data-testid="demo-loading">
            <Loader2 size={16} className="animate-spin text-gold" /> Loading demo data…
          </div>
        )}
        {err && (
          <div className="rounded-xl border border-sev-critical/40 bg-sev-critical/5 p-6 text-sm" data-testid="demo-error">
            {err}
          </div>
        )}

        {demo && (
          <div className="grid lg:grid-cols-[280px_1fr] gap-6">
            {/* Project list */}
            <aside className="space-y-2" data-testid="demo-project-list">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink/45 mb-2">Demo projects</div>
              {demo.projects.map((p) => {
                const Icon = TypeIcon(p.type);
                const isActive = p.id === activeProjectId;
                return (
                  <button
                    key={p.id}
                    onClick={() => setActiveProjectId(p.id)}
                    className={`w-full text-left rounded-lg border p-4 transition-colors ${
                      isActive ? "border-gold bg-gold/5" : "border-line bg-panel hover:border-gold/40"
                    }`}
                    data-testid={`demo-project-${p.id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={14} className="text-gold" />
                      <span className="text-[10px] uppercase tracking-wider text-gold font-semibold">{p.type}</span>
                    </div>
                    <div className="font-semibold text-sm text-ink leading-tight">{p.name}</div>
                    <div className="text-xs text-ink/55 mt-1">{p.target}</div>
                    <div className="flex items-center gap-1 mt-2.5 flex-wrap">
                      {["critical", "high", "medium", "low"].map((sev) => {
                        const n = (p.open_findings_count || {})[sev] || 0;
                        if (!n) return null;
                        return <SeverityChip key={sev} severity={sev} count={n} />;
                      })}
                    </div>
                  </button>
                );
              })}
            </aside>

            {/* Findings list */}
            <div data-testid="demo-findings-pane">
              <div className="rounded-xl border border-line bg-panel p-6 mb-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="font-display text-2xl">{active?.name}</h2>
                    <div className="text-xs text-ink/55 mt-1 font-mono">{active?.target}</div>
                  </div>
                  <div className="text-xs text-ink/55">
                    {findings.length} {findings.length === 1 ? "finding" : "findings"}
                  </div>
                </div>
              </div>

              {findings.length === 0 ? (
                <div className="rounded-xl border border-dashed border-line p-12 text-center text-ink/60 flex items-center justify-center gap-2">
                  <CheckCircle2 size={16} className="text-safe" />
                  No findings on this demo project. (Synthetic clean state.)
                </div>
              ) : (
                <ul className="space-y-3" data-testid="demo-findings-list">
                  {findings.map((f) => (
                    <li key={f.id} className="rounded-xl border border-line bg-panel p-5 hover:border-gold/40 transition-colors">
                      <div className="flex items-start gap-3">
                        <SeverityChip severity={f.severity} />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-ink">{f.title}</div>
                          <div className="text-xs text-ink/55 mt-1 font-mono truncate">{f.asset}</div>
                          {f.description && (
                            <div className="text-sm text-ink/75 mt-2 leading-relaxed line-clamp-2">{f.description}</div>
                          )}
                          <div className="flex flex-wrap gap-2 mt-3">
                            {f.cwe_id && (
                              <span className="text-[10px] uppercase tracking-wider border border-line bg-paper/50 rounded-full px-2 py-0.5 text-ink/60">{f.cwe_id}</span>
                            )}
                            {f.owasp_category && (
                              <span className="text-[10px] uppercase tracking-wider border border-gold/30 bg-gold/5 rounded-full px-2 py-0.5 text-gold/90">{f.owasp_category.split(" — ")[0]}</span>
                            )}
                            <span className="text-[10px] uppercase tracking-wider border border-line bg-paper/50 rounded-full px-2 py-0.5 text-ink/60">{f.scanner}</span>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
